import { Plan } from '@models/membership/Plan'
import { planSchema } from '@models/schema'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchPlanById, fetchPlans } from '@services/membership/PlanService'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<Plan> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'plan',
  initialState,
  reducers: {
    ...baseReducers,
    // Some lists (e.g. coupons) embed a trimmed plan with only its id and
    // translations. Merge per plan so a trimmed copy does not drop the fields
    // (program, price, limits) a full fetch already stored.
    getItemsSuccess: (state, action: PayloadAction<Record<number, Plan>>) => {
      Object.entries(action.payload).forEach(([id, plan]) => {
        const key = Number(id)
        state.items[key] = { ...state.items[key], ...plan }
      })
    },
  },
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getPlansSuccess,
  setQuery: setPlanQuery,
  reset: resetPlanState,
  resetQueryAndIds: resetPlanQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Plan }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectPlans = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedPlan = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectPlanById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectPlansByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedPlan =
  (plan: Plan | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Plan>(plan)))

export const getPlans = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchPlans(params),
    [planSchema],
    slice.actions
  )
export const getPlanWithId = (id: number) =>
  SBAPI.FetchDispatched<Plan>(fetchPlanById(id), planSchema, slice.actions)
